import type { MouseEventHandler, ReactNode } from "react";
import { Box } from "../../components/ui/box";
import type { Omittable } from "../../lib/props";
import { ROW_RUNGS } from "../../lib/row-rungs";

/**
 * The one list row a widget body renders — for anything that stacks one
 * pressable line per item inside a `WidgetCard`.
 *
 * Geometry is the whole point. The row's leading content sits ON the card's
 * content edge, level with the header's title above it, and the hover wash
 * BLEEDS `sm` past that edge on both sides. So the row carries a visible
 * cushion around its content without spending real inset on it. Rows that
 * instead pad `md` inside the body's own `md` push every leading avatar a full
 * gutter right of the title, which reads as an indented sub-list. Same trade
 * the custom-field rows and the properties band make: pull the surface out,
 * pad it back in.
 *
 * The host widget must NOT declare `bleed`. A bleeding body has no horizontal
 * padding, so on the grid — where the card CLIPS and draws a real border — the
 * row's content lands on that border and the wash is cut off at it. `bleed` is
 * for edge-to-edge tables that restore their own inset, never for these rows.
 * The stack presentation has no body inset either, but its card neither clips
 * nor draws an edge, so the same row reads correctly there: content level with
 * the header's title, wash spilling into the host's own padding exactly as the
 * header's own wash does.
 *
 * Left/right content placement is the caller's: this is a `justify="between"`
 * row, so a leading group and a trailing rail split it.
 *
 * The row stands on ONE rung, {@link LIST_ROW_PX}: a 20px line (a `sm` Text,
 * a chip, an `sm` entity tag) inside the `sm` padding. The rung is a floor,
 * not a cap, so a row whose content is shorter than a line — an icon alone,
 * a bar — still lands on the same pitch as its neighbours, and a band's
 * title line (`WidgetBandHeader`) is the same 36px, so headings and the rows
 * under them read as one column at one beat rather than two heights
 * interleaved.
 *
 * The row is never a `<button>`: it hosts real buttons (a trailing action
 * rail), and a button cannot nest inside a button. Its leading content is the
 * item's own interactive label; the row press is the convenience overlay.
 */
/** The list row's rung in px: a 20px line inside the row's `sm` padding.
 *  Every line in a widget list — a row, a band's title — stands on it. */
export const LIST_ROW_PX = ROW_RUNGS.listRow;

export type WidgetListRowProps = Omittable<{
  onClick?: MouseEventHandler<HTMLDivElement>;
  children: ReactNode;
}>;

export function WidgetListRow({ onClick, children }: WidgetListRowProps) {
  return (
    <Box
      direction="row"
      align="center"
      justify="between"
      gap="sm"
      marginXNegative="sm"
      paddingX="sm"
      paddingY="sm"
      minHeight={LIST_ROW_PX}
      rounded="sm"
      hoverBackground
      // The row owns the `row` scope so per-row chrome reveals with
      // `hoverReveal="row"`. It is deliberately not the caller's choice: the
      // enclosing `WidgetCard` owns `box`, and a row sharing that scope lights
      // every sibling row at once whenever the card is hovered.
      group="row"
      cursor={onClick ? "pointer" : undefined}
      onClick={onClick}
    >
      {children}
    </Box>
  );
}
